import { clsx } from 'clsx';
import { isNil, upperFirst } from 'lodash-es';

import styles from '../../styles/element.module.scss';

// Font Sizes
export const fontSizes = [
  'base',
  '3xs',
  '2xs',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
  '3xl',
  '4xl',
  '5xl'
] as const;
export type FontSizes = (typeof fontSizes)[number];

// Line Heights
export const lineHeights = ['sm', 'md', 'lg', 'xl', '2xl'] as const;
export type LineHeights = (typeof lineHeights)[number];

// Letter Spacings
export const letterSpacings = [
  'none',
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
  '3xl',
  '4xl'
] as const;
export type LetterSpacings = (typeof letterSpacings)[number];

// Font Weights
export const fontWeights = ['bold', 'demi', 'medium', 'book'] as const;
export type FontWeights = (typeof fontWeights)[number];

// Text Transforms
export const textTransforms = ['normal', 'allCaps', 'title', 'none'] as const;
export type TextTransforms = (typeof textTransforms)[number];

export interface TypographyProps {
  /** Overrides the variant's font size. */
  fontSize?: FontSizes;
  /** Overrides the variant's line height. */
  lineHeight?: LineHeights;
  /** Overrides the variant's letter spacing. */
  letterSpacing?: LetterSpacings;
  /** Overrides the variant's font weight. */
  fontWeight?: FontWeights;
  /** Overrides the variant's text transform. */
  textTransform?: TextTransforms;
}

const typographyClassNames = (
  styles: Record<string, string>,
  propKey: string,
  propValue: string | undefined
): Record<string, boolean> => {
  const classNames: Record<string, boolean> = {};

  if (!isNil(propValue)) {
    classNames[
      styles[`${propKey}${upperFirst(propValue)}`] as keyof typeof classNames
    ] = true;
  }

  return classNames;
};

export const typographyHelpers = {
  defaultProps: {
    fontSize: undefined,
    lineHeight: undefined,
    letterSpacing: undefined,
    fontWeight: undefined,
    textTransform: undefined
  },
  classnames: (props: TypographyProps): string => {
    return clsx({
      ...typographyClassNames(styles, 'fontSize', props.fontSize),
      ...typographyClassNames(styles, 'lineHeight', props.lineHeight),
      ...typographyClassNames(styles, 'letterSpacing', props.letterSpacing),
      ...typographyClassNames(styles, 'fontWeight', props.fontWeight),
      ...typographyClassNames(styles, 'textTransform', props.textTransform)
    });
  },
  base: ({
    fontSize,
    lineHeight,
    letterSpacing,
    fontWeight,
    textTransform,
    ...props
  }: TypographyProps & unknown) => props
};
